import { observer } from "mobx-react";
import React, { useMemo, useState } from "react";
import copy from "copy-to-clipboard";
import { BugReportOutlined, ExpandMoreRounded } from "@mui/icons-material";
import {
    Box,
    Collapse,
    Stack,
    SxProps,
    Theme,
    Tooltip,
    Typography,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { useStore } from "utils/useStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { Suggestion } from "../Suggestions/Suggestion";
import AcxDialog from "components/UI/Dialog/AcxDialog";

type DebugEntry = {
    id?: string;
    parentId?: string | null;
    entryType?: string;
    timestampUtc?: string;
    category?: string;
    message?: string;
    elapsedMilliseconds?: number | null;
    metadata?: unknown;
};

type DebugTraceEntry = DebugEntry & {
    key: string;
    children: DebugTraceEntry[];
};

type NormalizedTokenUsage = {
    activityName: string;
    modelName: string;
    modelDeploymentName: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    cachedInputTokens?: number;
    reasoningTokens?: number;
    inputTextTokens?: number;
    inputAudioTokens?: number;
    outputTextTokens?: number;
    outputAudioTokens?: number;
    acceptedPredictionTokens?: number;
    rejectedPredictionTokens?: number;
    additionalCounts: Record<string, number>;
};

type OptionalTokenCountKey =
    | "cachedInputTokens"
    | "reasoningTokens"
    | "inputTextTokens"
    | "inputAudioTokens"
    | "outputTextTokens"
    | "outputAudioTokens"
    | "acceptedPredictionTokens"
    | "rejectedPredictionTokens";

type RawLlmRequest = {
    title: string;
    body: string;
};

type TokenUsageTotals = {
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
    reasoningTokens: number;
    totalTokens: number;
    inputTextTokens: number;
    inputAudioTokens: number;
    outputTextTokens: number;
    outputAudioTokens: number;
    acceptedPredictionTokens: number;
    rejectedPredictionTokens: number;
    additionalCounts: Record<string, number>;
};

interface ViewDebugActionProps {
    args: unknown;
    remove?: () => void;
    trigger?: "suggestion" | "icon";
    iconSx?: SxProps<Theme>;
}

const TIME_TO_FIRST_TOKEN_MESSAGE = "Response.TimeToFirstTokenReturned";

export const ViewDebugAction = observer(
    ({ args, trigger = "suggestion", iconSx }: ViewDebugActionProps) => {
        const [modalOpen, setModalOpen] = useState(false);
        const [expandedEntryKeys, setExpandedEntryKeys] = useState<Set<string>>(
            () => new Set(),
        );
        const [promptModal, setPromptModal] = useState<{
            title: string;
            prompt: string;
        } | null>(null);
        const messageStore = useStore(MessageStore);

        const debugEntries = useMemo(
            () => (Array.isArray(args) ? (args as DebugEntry[]) : []),
            [args],
        );

        const timeToFirstTokenEntry = useMemo(
            () => debugEntries.find(isTimeToFirstTokenEntry),
            [debugEntries],
        );

        const tokenUsageEntry = useMemo(
            () => debugEntries.find(isTokenUsageEntry),
            [debugEntries],
        );

        const tokenUsages = useMemo(
            () => getTokenUsagesFromMetadata(tokenUsageEntry?.metadata),
            [tokenUsageEntry],
        );

        const rawLlmRequests = useMemo(
            () =>
                debugEntries
                    .filter(isRawLlmRequestEntry)
                    .map(getRawLlmRequestFromEntry)
                    .filter(
                        (request): request is RawLlmRequest =>
                            request !== undefined,
                    ),
            [debugEntries],
        );

        const tokenTotals = useMemo(
            () => summarizeTokenUsages(tokenUsages),
            [tokenUsages],
        );

        const debugTraceEntries = useMemo(
            () =>
                buildDebugTraceEntries(
                    debugEntries.filter(
                        (entry) =>
                            !isTimeToFirstTokenEntry(entry) &&
                            !isTokenUsageEntry(entry) &&
                            !isRawLlmRequestEntry(entry),
                    ),
                ),
            [debugEntries],
        );

        const timeToFirstTokenSpanId = timeToFirstTokenEntry?.parentId;

        const expandableEntryKeys = useMemo(
            () => getExpandableEntryKeys(debugTraceEntries),
            [debugTraceEntries],
        );

        const copyText = useMemo(
            () =>
                formatDebugEntriesForCopy(
                    debugTraceEntries,
                    timeToFirstTokenEntry,
                    tokenUsages,
                    rawLlmRequests,
                ),
            [
                debugTraceEntries,
                rawLlmRequests,
                timeToFirstTokenEntry,
                tokenUsages,
            ],
        );

        const toggleEntry = (entryKey: string) => {
            setExpandedEntryKeys((current) => {
                const next = new Set(current);
                if (next.has(entryKey)) {
                    next.delete(entryKey);
                } else {
                    next.add(entryKey);
                }

                return next;
            });
        };

        const renderEntry = (entry: DebugTraceEntry, depth = 0) => {
            const isExpanded = expandedEntryKeys.has(entry.key);
            const description = getCompactDescription(entry);
            const toolLabels = getToolLabelsForEntry(entry);
            const hasChildren = entry.children.length > 0;
            const hasMetadata =
                entry.metadata !== undefined && entry.metadata !== null;
            const rawPromptMetadata = getRawPromptMetadata(entry.metadata);
            const displayMetadata = rawPromptMetadata
                ? formatMetadataForDisplay(entry.metadata)
                : formatMetadata(entry.metadata);
            const canExpand = hasChildren || hasMetadata;
            const isTimeToFirstTokenSpan =
                entry.id !== undefined && entry.id === timeToFirstTokenSpanId;

            return (
                <Box
                    key={entry.key}
                    sx={(theme) => ({
                        border: isTimeToFirstTokenSpan
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.gray[200]}`,
                        borderRadius: "4px",
                        overflow: "hidden",
                        boxShadow: isTimeToFirstTokenSpan
                            ? `0 0 0 2px ${theme.palette.primary.light}`
                            : undefined,
                        backgroundColor:
                            isTimeToFirstTokenSpan
                                ? theme.palette.action.hover
                                : hasChildren || entry.entryType === "Span"
                                  ? theme.palette.gray[50]
                                  : "transparent",
                    })}
                >
                    <Box
                        role={canExpand ? "button" : undefined}
                        tabIndex={canExpand ? 0 : undefined}
                        onClick={() => canExpand && toggleEntry(entry.key)}
                        onKeyDown={(event) => {
                            if (
                                canExpand &&
                                (event.key === "Enter" || event.key === " ")
                            ) {
                                event.preventDefault();
                                toggleEntry(entry.key);
                            }
                        }}
                        sx={(theme) => ({
                            display: "grid",
                            gridTemplateColumns:
                                "minmax(220px, 1fr) 112px 150px 92px 28px",
                            columnGap: 1,
                            alignItems: "center",
                            padding: "6px 10px",
                            cursor: canExpand ? "pointer" : "default",
                            backgroundColor: isExpanded
                                ? theme.palette.gray[100]
                                : "transparent",
                            "&:hover": {
                                backgroundColor: canExpand
                                    ? theme.palette.gray[100]
                                    : undefined,
                            },
                        })}
                    >
                        <Box minWidth={0} sx={{ paddingLeft: depth * 2 }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                                minWidth={0}
                            >
                                <Tooltip title={entry.message || ""}>
                                    <Typography
                                        fontWeight={hasChildren ? 700 : 600}
                                        fontSize="13px"
                                        noWrap
                                    >
                                        {entry.message ||
                                            entry.category ||
                                            "Debug"}
                                    </Typography>
                                </Tooltip>
                                {toolLabels.map((toolLabel) => (
                                    <Tooltip
                                        key={`${entry.key}-${toolLabel}`}
                                        title={toolLabel}
                                    >
                                        <Box
                                            component="span"
                                            sx={(theme) => ({
                                                border: `1px solid ${theme.palette.gray[300]}`,
                                                borderRadius: "4px",
                                                color: theme.palette.gray[700],
                                                backgroundColor:
                                                    theme.palette.common.white,
                                                fontFamily: "monospace",
                                                fontSize: "10px",
                                                fontWeight: 700,
                                                lineHeight: "14px",
                                                maxWidth: "180px",
                                                overflow: "hidden",
                                                padding: "0 4px",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            })}
                                        >
                                            {toolLabel}
                                        </Box>
                                    </Tooltip>
                                ))}
                                {isTimeToFirstTokenSpan && (
                                    <Box
                                        component="span"
                                        sx={(theme) => ({
                                            border: `1px solid ${theme.palette.primary.main}`,
                                            borderRadius: "4px",
                                            color: theme.palette.primary.main,
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            lineHeight: "14px",
                                            padding: "0 4px",
                                            flexShrink: 0,
                                            whiteSpace: "nowrap",
                                        })}
                                    >
                                        Time to First Token
                                    </Box>
                                )}
                            </Box>
                            {(description ||
                                hasChildren ||
                                isTimeToFirstTokenSpan) && (
                                <Typography
                                    fontSize="12px"
                                    color="text.secondary"
                                    noWrap
                                >
                                    {[
                                        hasChildren
                                            ? `${entry.children.length} child step${
                                                  entry.children.length === 1
                                                      ? ""
                                                      : "s"
                                              }`
                                            : "",
                                        isTimeToFirstTokenSpan
                                            ? "first token returned here"
                                            : "",
                                        description,
                                    ]
                                        .filter(Boolean)
                                        .join(" - ")}
                                </Typography>
                            )}
                        </Box>
                        <Typography fontSize="12px" color="text.secondary">
                            {entry.category || "Debug"}
                        </Typography>
                        <Typography fontSize="12px" color="text.secondary">
                            {formatTimestamp(entry.timestampUtc)}
                        </Typography>
                        <Typography
                            fontSize="12px"
                            fontWeight={700}
                            textAlign="right"
                        >
                            {formatMilliseconds(entry.elapsedMilliseconds)}
                        </Typography>
                        {canExpand && (
                            <ExpandMoreRounded
                                sx={{
                                    fontSize: 18,
                                    transform: isExpanded
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                    transition: "transform 120ms ease",
                                }}
                            />
                        )}
                    </Box>
                    <Collapse in={isExpanded}>
                        <Box sx={{ padding: "8px 10px 10px" }}>
                            <Stack spacing={0.75}>
                                {rawPromptMetadata && (
                                    <Box>
                                        <AcxButton
                                            onClick={() =>
                                                setPromptModal({
                                                    title: getRawPromptTitle(
                                                        entry,
                                                        rawPromptMetadata,
                                                    ),
                                                    prompt:
                                                        rawPromptMetadata.rawPrompt,
                                                })
                                            }
                                        >
                                            View raw prompt
                                        </AcxButton>
                                    </Box>
                                )}
                                {hasMetadata && displayMetadata && (
                                    <Box
                                        component="pre"
                                        sx={{
                                            margin: 0,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {displayMetadata}
                                    </Box>
                                )}
                                {hasChildren && (
                                    <Stack spacing={0.75}>
                                        {entry.children.map((child) =>
                                            renderEntry(child, depth + 1),
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    </Collapse>
                </Box>
            );
        };

        return (
            <>
                {trigger === "icon" ? (
                    <Tooltip title="View Debug">
                        <BugReportOutlined
                            sx={iconSx}
                            onClick={() => setModalOpen(true)}
                            id="kbo-view-debug"
                        />
                    </Tooltip>
                ) : (
                    <Suggestion onClick={() => setModalOpen(true)}>
                        View Debug
                    </Suggestion>
                )}
                <AcxDialog
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title="KBo Debug"
                    maxWidth="xl"
                    dialogContentChildren={
                        <Stack spacing={2}>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={1}>
                                    <AcxButton
                                        disabled={debugEntries.length === 0}
                                        onClick={() => {
                                            copy(copyText);
                                            messageStore.logInfo(
                                                "Copied to clipboard",
                                            );
                                        }}
                                    >
                                        Copy to clipboard
                                    </AcxButton>
                                    <AcxButton
                                        disabled={
                                            expandableEntryKeys.length === 0
                                        }
                                        onClick={() =>
                                            setExpandedEntryKeys(
                                                new Set(expandableEntryKeys),
                                            )
                                        }
                                    >
                                        Expand all
                                    </AcxButton>
                                    <AcxButton
                                        disabled={expandedEntryKeys.size === 0}
                                        onClick={() =>
                                            setExpandedEntryKeys(new Set())
                                        }
                                    >
                                        Collapse all
                                    </AcxButton>
                                </Stack>
                                {timeToFirstTokenEntry && (
                                    <Box
                                        sx={(theme) => ({
                                            border: `1px solid ${theme.palette.gray[200]}`,
                                            borderRadius: "4px",
                                            padding: "4px 8px",
                                            textAlign: "right",
                                            minWidth: "140px",
                                        })}
                                    >
                                        <Typography
                                            fontSize="11px"
                                            color="text.secondary"
                                        >
                                            Time to First Token
                                        </Typography>
                                        <Typography
                                            fontSize="13px"
                                            fontWeight={700}
                                        >
                                            {formatMilliseconds(
                                                timeToFirstTokenEntry.elapsedMilliseconds,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                            {tokenUsages.length > 0 && (
                                <Box
                                    sx={(theme) => ({
                                        border: `1px solid ${theme.palette.gray[200]}`,
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                    })}
                                >
                                    <Box sx={{ overflowX: "auto" }}>
                                        <Box sx={{ minWidth: "880px" }}>
                                            <Box
                                                sx={(theme) => ({
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                        "minmax(180px, 1.2fr) minmax(150px, 1fr) repeat(5, minmax(86px, 96px))",
                                                    columnGap: 1,
                                                    padding: "6px 10px",
                                                    backgroundColor:
                                                        theme.palette.gray[50],
                                                })}
                                            >
                                                <Typography
                                                    fontSize="11px"
                                                    color="text.secondary"
                                                >
                                                    Activity
                                                </Typography>
                                                <Typography
                                                    fontSize="11px"
                                                    color="text.secondary"
                                                >
                                                    Model
                                                </Typography>
                                                <Typography
                                                    fontSize="11px"
                                                    color="text.secondary"
                                                    textAlign="right"
                                                >
                                                    Input
                                                </Typography>
                                                <Typography
                                                    fontSize="11px"
                                                    color="text.secondary"
                                                    textAlign="right"
                                                >
                                                    Cached
                                                </Typography>
                                                <Typography
                                                    fontSize="11px"
                                                    color="text.secondary"
                                                    textAlign="right"
                                                >
                                                    Output
                                                </Typography>
                                                <Typography
                                                    fontSize="11px"
                                                    color="text.secondary"
                                                    textAlign="right"
                                                >
                                                    Reasoning
                                                </Typography>
                                                <Typography
                                                    fontSize="11px"
                                                    color="text.secondary"
                                                    textAlign="right"
                                                >
                                                    Total
                                                </Typography>
                                            </Box>
                                            {tokenUsages.map((usage, index) => {
                                                const detailText =
                                                    formatTokenDetailBreakdown(
                                                        usage,
                                                    );

                                                return (
                                                    <Box
                                                        key={`${usage.activityName}-${index}`}
                                                        sx={(theme) => ({
                                                            borderTop:
                                                                index === 0
                                                                    ? undefined
                                                                    : `1px solid ${theme.palette.gray[100]}`,
                                                        })}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display:
                                                                    "grid",
                                                                gridTemplateColumns:
                                                                    "minmax(180px, 1.2fr) minmax(150px, 1fr) repeat(5, minmax(86px, 96px))",
                                                                columnGap: 1,
                                                                padding:
                                                                    detailText
                                                                        ? "6px 10px 2px"
                                                                        : "6px 10px",
                                                            }}
                                                        >
                                                            <Typography
                                                                fontSize="12px"
                                                                noWrap
                                                            >
                                                                {
                                                                    usage.activityName
                                                                }
                                                            </Typography>
                                                            <Typography
                                                                fontSize="12px"
                                                                color="text.secondary"
                                                                noWrap
                                                            >
                                                                {usage.modelDeploymentName ||
                                                                    usage.modelName}
                                                            </Typography>
                                                            <Typography
                                                                fontSize="12px"
                                                                textAlign="right"
                                                            >
                                                                {formatTokenCount(
                                                                    usage.inputTokens,
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                fontSize="12px"
                                                                textAlign="right"
                                                            >
                                                                {formatOptionalTokenCount(
                                                                    usage.cachedInputTokens,
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                fontSize="12px"
                                                                textAlign="right"
                                                            >
                                                                {formatTokenCount(
                                                                    usage.outputTokens,
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                fontSize="12px"
                                                                textAlign="right"
                                                            >
                                                                {formatOptionalTokenCount(
                                                                    usage.reasoningTokens,
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                fontSize="12px"
                                                                fontWeight={700}
                                                                textAlign="right"
                                                            >
                                                                {formatTokenCount(
                                                                    usage.totalTokens,
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                        {detailText && (
                                                            <Typography
                                                                fontSize="11px"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontFamily:
                                                                        "monospace",
                                                                    padding:
                                                                        "0 10px 6px",
                                                                }}
                                                            >
                                                                {detailText}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                            <Box
                                                sx={(theme) => ({
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                        "minmax(180px, 1.2fr) minmax(150px, 1fr) repeat(5, minmax(86px, 96px))",
                                                    columnGap: 1,
                                                    padding: "6px 10px",
                                                    borderTop: `1px solid ${theme.palette.gray[200]}`,
                                                })}
                                            >
                                                <Typography
                                                    fontSize="12px"
                                                    fontWeight={700}
                                                >
                                                    Total
                                                </Typography>
                                                <Box />
                                                <Typography
                                                    fontSize="12px"
                                                    fontWeight={700}
                                                    textAlign="right"
                                                >
                                                    {formatTokenCount(
                                                        tokenTotals.inputTokens,
                                                    )}
                                                </Typography>
                                                <Typography
                                                    fontSize="12px"
                                                    fontWeight={700}
                                                    textAlign="right"
                                                >
                                                    {formatOptionalTokenCount(
                                                        getReportedTokenTotal(
                                                            tokenUsages,
                                                            tokenTotals,
                                                            "cachedInputTokens",
                                                        ),
                                                    )}
                                                </Typography>
                                                <Typography
                                                    fontSize="12px"
                                                    fontWeight={700}
                                                    textAlign="right"
                                                >
                                                    {formatTokenCount(
                                                        tokenTotals.outputTokens,
                                                    )}
                                                </Typography>
                                                <Typography
                                                    fontSize="12px"
                                                    fontWeight={700}
                                                    textAlign="right"
                                                >
                                                    {formatOptionalTokenCount(
                                                        getReportedTokenTotal(
                                                            tokenUsages,
                                                            tokenTotals,
                                                            "reasoningTokens",
                                                        ),
                                                    )}
                                                </Typography>
                                                <Typography
                                                    fontSize="12px"
                                                    fontWeight={700}
                                                    textAlign="right"
                                                >
                                                    {formatTokenCount(
                                                        tokenTotals.totalTokens,
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            {rawLlmRequests.length > 0 && (
                                <Stack spacing={1}>
                                    {rawLlmRequests.map((request, index) => (
                                        <Box
                                            key={`${request.title}-${index}`}
                                            sx={(theme) => ({
                                                border: `1px solid ${theme.palette.gray[200]}`,
                                                borderRadius: "4px",
                                                overflow: "hidden",
                                            })}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={(theme) => ({
                                                    padding: "6px 10px",
                                                    backgroundColor:
                                                        theme.palette.gray[50],
                                                })}
                                            >
                                                <Typography
                                                    fontSize="13px"
                                                    fontWeight={700}
                                                >
                                                    {request.title}
                                                </Typography>
                                                <AcxButton
                                                    onClick={() => {
                                                        copy(request.body);
                                                        messageStore.logInfo(
                                                            "Copied to clipboard",
                                                        );
                                                    }}
                                                >
                                                    Copy request
                                                </AcxButton>
                                            </Stack>
                                            <Box
                                                component="pre"
                                                sx={{
                                                    fontSize: "12px",
                                                    margin: 0,
                                                    maxHeight: "360px",
                                                    overflow: "auto",
                                                    padding: "10px",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {request.body}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                            <Stack spacing={0.75}>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "minmax(220px, 1fr) 112px 150px 92px 28px",
                                        columnGap: 1,
                                        padding: "0 10px",
                                    }}
                                >
                                    <Typography
                                        fontSize="11px"
                                        color="text.secondary"
                                    >
                                        Title
                                    </Typography>
                                    <Typography
                                        fontSize="11px"
                                        color="text.secondary"
                                    >
                                        Category
                                    </Typography>
                                    <Typography
                                        fontSize="11px"
                                        color="text.secondary"
                                    >
                                        Date
                                    </Typography>
                                    <Typography
                                        fontSize="11px"
                                        color="text.secondary"
                                        textAlign="right"
                                    >
                                        Duration
                                    </Typography>
                                </Box>
                                {debugTraceEntries.map((entry) =>
                                    renderEntry(entry),
                                )}
                            </Stack>
                        </Stack>
                    }
                />
                <AcxDialog
                    isOpen={promptModal !== null}
                    onClose={() => setPromptModal(null)}
                    title={promptModal?.title ?? "Raw prompt"}
                    maxWidth="xl"
                    dialogContentChildren={
                        <Box
                            component="pre"
                            sx={{
                                margin: 0,
                                maxHeight: "70vh",
                                overflow: "auto",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontSize: "12px",
                            }}
                        >
                            {promptModal?.prompt ?? ""}
                        </Box>
                    }
                />
            </>
        );
    },
);

function buildDebugTraceEntries(entries: DebugEntry[]): DebugTraceEntry[] {
    const nodes: Array<DebugTraceEntry & { originalIndex: number }> = entries
        .map((entry, index) => ({
            ...entry,
            key: entry.id || `${entry.timestampUtc || "debug"}-${index}`,
            children: [] as DebugTraceEntry[],
            originalIndex: index,
        }))
        .sort(compareDebugEntries);

    const nodesById = new Map<string, DebugTraceEntry>();
    nodes.forEach((node) => {
        if (node.id) {
            nodesById.set(node.id, node);
        }
    });

    const roots: DebugTraceEntry[] = [];
    nodes.forEach((node) => {
        if (node.parentId) {
            const parent = nodesById.get(node.parentId);
            if (parent) {
                parent.children.push(node);
                return;
            }
        }

        roots.push(node);
    });

    sortDebugTraceEntries(roots);
    return flattenAgenticLoopWrapperEntries(roots);
}

function compareDebugEntries(
    a: DebugTraceEntry & { originalIndex: number },
    b: DebugTraceEntry & { originalIndex: number },
) {
    const aTimestamp = Date.parse(a.timestampUtc || "");
    const bTimestamp = Date.parse(b.timestampUtc || "");

    if (Number.isNaN(aTimestamp) || Number.isNaN(bTimestamp)) {
        return a.originalIndex - b.originalIndex;
    }

    return aTimestamp - bTimestamp || a.originalIndex - b.originalIndex;
}

function sortDebugTraceEntries(entries: DebugTraceEntry[]) {
    entries.sort((a, b) => {
        const aTimestamp = Date.parse(a.timestampUtc || "");
        const bTimestamp = Date.parse(b.timestampUtc || "");

        if (Number.isNaN(aTimestamp) || Number.isNaN(bTimestamp)) {
            return 0;
        }

        return aTimestamp - bTimestamp;
    });
    entries.forEach((entry) => sortDebugTraceEntries(entry.children));
}

function flattenAgenticLoopWrapperEntries(
    entries: DebugTraceEntry[],
): DebugTraceEntry[] {
    return entries.map((entry) => {
        const children = flattenAgenticLoopWrapperEntries(entry.children);

        if (
            isAgenticLoopStrategyWrapper(entry) &&
            children.length === 1 &&
            isAgenticLoopSpan(children[0])
        ) {
            const agenticLoop = children[0];
            return {
                ...entry,
                children: agenticLoop.children,
                metadata: mergeMetadata(entry.metadata, agenticLoop.metadata),
            };
        }

        return {
            ...entry,
            children,
        };
    });
}

function isAgenticLoopStrategyWrapper(entry: DebugTraceEntry) {
    return (
        entry.category === "Timing" &&
        entry.message === "ProcessStreamingChat.ExecuteStrategy.AgenticLoop"
    );
}

function isAgenticLoopSpan(entry: DebugTraceEntry) {
    return entry.category === "Agentic Loop" && entry.message === "Agentic loop";
}

function mergeMetadata(parentMetadata: unknown, childMetadata: unknown) {
    if (isRecord(parentMetadata) && isRecord(childMetadata)) {
        return {
            ...childMetadata,
            ...parentMetadata,
        };
    }

    return parentMetadata ?? childMetadata;
}

function getExpandableEntryKeys(entries: DebugTraceEntry[]): string[] {
    return entries.flatMap((entry) => [
        ...(entry.children.length > 0 ||
        (entry.metadata !== undefined && entry.metadata !== null)
            ? [entry.key]
            : []),
        ...getExpandableEntryKeys(entry.children),
    ]);
}

function formatDebugEntriesForCopy(
    entries: DebugTraceEntry[],
    timeToFirstTokenEntry?: DebugEntry,
    tokenUsages: NormalizedTokenUsage[] = [],
    rawLlmRequests: RawLlmRequest[] = [],
) {
    return [
        timeToFirstTokenEntry
            ? formatTimeToFirstTokenForCopy(timeToFirstTokenEntry)
            : "",
        tokenUsages.length > 0 ? formatTokenUsagesForCopy(tokenUsages) : "",
        ...rawLlmRequests.map(
            (request) => `${request.title}\n${request.body}`,
        ),
        ...entries.map(formatEntryForCopy),
    ]
        .filter(Boolean)
        .join("\n\n");
}

function formatTokenUsagesForCopy(tokenUsages: NormalizedTokenUsage[]) {
    const totals = summarizeTokenUsages(tokenUsages);
    const lines = ["Token Usage"];

    tokenUsages.forEach((usage) => {
        lines.push(
            [
                `${usage.activityName}:`,
                `${formatTokenCount(usage.inputTokens)} input`,
                `${formatOptionalTokenCount(usage.cachedInputTokens)} cached input`,
                `${formatTokenCount(usage.outputTokens)} output`,
                `${formatOptionalTokenCount(usage.reasoningTokens)} reasoning`,
                `${formatTokenCount(usage.totalTokens)} total`,
                `(${usage.modelDeploymentName || usage.modelName})`,
            ].join(" "),
        );

        const detailText = formatTokenDetailBreakdown(usage);
        if (detailText) {
            lines.push(`  ${detailText}`);
        }
    });

    lines.push(
        [
            "Total:",
            `${formatTokenCount(totals.inputTokens)} input`,
            `${formatOptionalTokenCount(
                getReportedTokenTotal(
                    tokenUsages,
                    totals,
                    "cachedInputTokens",
                ),
            )} cached input`,
            `${formatTokenCount(totals.outputTokens)} output`,
            `${formatOptionalTokenCount(
                getReportedTokenTotal(tokenUsages, totals, "reasoningTokens"),
            )} reasoning`,
            `${formatTokenCount(totals.totalTokens)} total`,
        ].join(" "),
    );

    return lines.join("\n");
}

function formatTokenDetailBreakdown(usage: NormalizedTokenUsage) {
    const detailItems: string[] = [];
    const optionalFields: Array<{
        key: OptionalTokenCountKey;
        label: string;
    }> = [
        { key: "inputTextTokens", label: "input text" },
        { key: "inputAudioTokens", label: "input audio" },
        { key: "outputTextTokens", label: "output text" },
        { key: "outputAudioTokens", label: "output audio" },
        { key: "acceptedPredictionTokens", label: "accepted prediction" },
        { key: "rejectedPredictionTokens", label: "rejected prediction" },
    ];

    optionalFields.forEach(({ key, label }) => {
        const value = usage[key];
        if (typeof value === "number") {
            detailItems.push(`${label}: ${formatTokenCount(value)}`);
        }
    });

    Object.entries(usage.additionalCounts).forEach(([key, value]) => {
        detailItems.push(`${formatAdditionalTokenCountKey(key)}: ${formatTokenCount(value)}`);
    });

    return detailItems.join(" - ");
}

function formatAdditionalTokenCountKey(key: string) {
    return key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .toLowerCase();
}

function formatTokenCount(value: number) {
    return value.toLocaleString();
}

function formatOptionalTokenCount(value?: number) {
    return typeof value === "number" ? formatTokenCount(value) : "-";
}

function getReportedTokenTotal(
    tokenUsages: NormalizedTokenUsage[],
    totals: TokenUsageTotals,
    key: OptionalTokenCountKey,
) {
    return tokenUsages.some((usage) => typeof usage[key] === "number")
        ? totals[key]
        : undefined;
}

function formatTimeToFirstTokenForCopy(entry: DebugEntry) {
    const lines = [`[Timing] ${TIME_TO_FIRST_TOKEN_MESSAGE}`];

    if (typeof entry.elapsedMilliseconds === "number") {
        lines.push(`Elapsed: ${formatMilliseconds(entry.elapsedMilliseconds)}`);
    }

    if (entry.timestampUtc) {
        lines.push(`Timestamp: ${entry.timestampUtc}`);
    }

    if (entry.parentId) {
        lines.push(`Span ID: ${entry.parentId}`);
    }

    if (entry.metadata !== undefined && entry.metadata !== null) {
        lines.push(`Metadata: ${formatMetadata(entry.metadata)}`);
    }

    return lines.join("\n");
}

function formatEntryForCopy(entry: DebugTraceEntry, depth = 0): string {
    const indent = "  ".repeat(depth);
    const lines = [
        `${indent}[${entry.category || "Debug"}] ${entry.message || ""}`,
    ];

    if (typeof entry.elapsedMilliseconds === "number") {
        lines.push(`${indent}Elapsed: ${formatMilliseconds(entry.elapsedMilliseconds)}`);
    }

    if (entry.timestampUtc) {
        lines.push(`${indent}Timestamp: ${entry.timestampUtc}`);
    }

    if (entry.metadata !== undefined && entry.metadata !== null) {
        lines.push(`${indent}Metadata: ${formatMetadata(entry.metadata)}`);
    }

    if (entry.children.length > 0) {
        lines.push(
            ...entry.children.map((child) =>
                formatEntryForCopy(child, depth + 1),
            ),
        );
    }

    return lines.join("\n");
}

function isTimeToFirstTokenEntry(entry: DebugEntry) {
    return entry.message === TIME_TO_FIRST_TOKEN_MESSAGE;
}

function isTokenUsageEntry(entry: DebugEntry) {
    return (
        entry.category === "Tokens" &&
        entry.message === "KBo token usage for this request"
    );
}

function isRawLlmRequestEntry(entry: DebugEntry) {
    return entry.category === "LLM" && entry.message === "Raw LLM request";
}

function getTokenUsagesFromMetadata(
    metadata: unknown,
): NormalizedTokenUsage[] {
    if (!isRecord(metadata)) {
        return [];
    }

    const tokenUsages = metadata.tokenUsages ?? metadata.TokenUsages;
    if (Array.isArray(tokenUsages)) {
        return tokenUsages
            .map(normalizeTokenUsage)
            .filter(
                (usage): usage is NormalizedTokenUsage => usage !== undefined,
            );
    }

    const inputTokens = getNumberProperty(metadata, "inputTokens", "InputTokens");
    const outputTokens = getNumberProperty(
        metadata,
        "outputTokens",
        "OutputTokens",
    );

    if (inputTokens === 0 && outputTokens === 0) {
        return [];
    }

    return [
        {
            activityName: "KBo request",
            modelName: "",
            modelDeploymentName: "",
            inputTokens,
            outputTokens,
            totalTokens: inputTokens + outputTokens,
            additionalCounts: {},
        },
    ];
}

function normalizeTokenUsage(value: unknown): NormalizedTokenUsage | undefined {
    if (!isRecord(value)) {
        return undefined;
    }

    const inputTokens = getNumberProperty(value, "inputTokens", "InputTokens");
    const outputTokens = getNumberProperty(
        value,
        "outputTokens",
        "OutputTokens",
    );
    const totalTokens =
        getOptionalNumberProperty(value, "totalTokens", "TotalTokens") ??
        inputTokens + outputTokens;

    return {
        activityName:
            getStringProperty(value, "activityName", "ActivityName") ||
            "KBo request",
        modelName: getStringProperty(value, "modelName", "ModelName"),
        modelDeploymentName: getStringProperty(
            value,
            "modelDeploymentName",
            "ModelDeploymentName",
        ),
        inputTokens,
        outputTokens,
        totalTokens,
        cachedInputTokens: getOptionalNumberProperty(
            value,
            "cachedInputTokens",
            "CachedInputTokens",
        ),
        reasoningTokens: getOptionalNumberProperty(
            value,
            "reasoningTokens",
            "ReasoningTokens",
        ),
        inputTextTokens: getOptionalNumberProperty(
            value,
            "inputTextTokens",
            "InputTextTokens",
        ),
        inputAudioTokens: getOptionalNumberProperty(
            value,
            "inputAudioTokens",
            "InputAudioTokens",
        ),
        outputTextTokens: getOptionalNumberProperty(
            value,
            "outputTextTokens",
            "OutputTextTokens",
        ),
        outputAudioTokens: getOptionalNumberProperty(
            value,
            "outputAudioTokens",
            "OutputAudioTokens",
        ),
        acceptedPredictionTokens: getOptionalNumberProperty(
            value,
            "acceptedPredictionTokens",
            "AcceptedPredictionTokens",
        ),
        rejectedPredictionTokens: getOptionalNumberProperty(
            value,
            "rejectedPredictionTokens",
            "RejectedPredictionTokens",
        ),
        additionalCounts: getNumberRecordProperty(
            value,
            "additionalCounts",
            "AdditionalCounts",
        ),
    };
}

function summarizeTokenUsages(
    tokenUsages: NormalizedTokenUsage[],
): TokenUsageTotals {
    return tokenUsages.reduce<TokenUsageTotals>(
        (totals, usage) => ({
            inputTokens: totals.inputTokens + usage.inputTokens,
            cachedInputTokens:
                totals.cachedInputTokens + (usage.cachedInputTokens ?? 0),
            outputTokens: totals.outputTokens + usage.outputTokens,
            reasoningTokens:
                totals.reasoningTokens + (usage.reasoningTokens ?? 0),
            totalTokens: totals.totalTokens + usage.totalTokens,
            inputTextTokens:
                totals.inputTextTokens + (usage.inputTextTokens ?? 0),
            inputAudioTokens:
                totals.inputAudioTokens + (usage.inputAudioTokens ?? 0),
            outputTextTokens:
                totals.outputTextTokens + (usage.outputTextTokens ?? 0),
            outputAudioTokens:
                totals.outputAudioTokens + (usage.outputAudioTokens ?? 0),
            acceptedPredictionTokens:
                totals.acceptedPredictionTokens +
                (usage.acceptedPredictionTokens ?? 0),
            rejectedPredictionTokens:
                totals.rejectedPredictionTokens +
                (usage.rejectedPredictionTokens ?? 0),
            additionalCounts: mergeAdditionalTokenCounts(
                totals.additionalCounts,
                usage.additionalCounts,
            ),
        }),
        {
            inputTokens: 0,
            cachedInputTokens: 0,
            outputTokens: 0,
            reasoningTokens: 0,
            totalTokens: 0,
            inputTextTokens: 0,
            inputAudioTokens: 0,
            outputTextTokens: 0,
            outputAudioTokens: 0,
            acceptedPredictionTokens: 0,
            rejectedPredictionTokens: 0,
            additionalCounts: {},
        },
    );
}

function getRawLlmRequestFromEntry(
    entry: DebugEntry,
    index: number,
): RawLlmRequest | undefined {
    const requestBody = getRawLlmRequestFromMetadata(entry.metadata);
    if (!requestBody) {
        return undefined;
    }

    const metadata = isRecord(entry.metadata) ? entry.metadata : undefined;
    const turn = metadata
        ? getOptionalNumberProperty(metadata, "turn", "Turn")
        : undefined;
    const modelName = metadata
        ? getStringProperty(metadata, "modelName", "ModelName")
        : "";
    const titleParts = [
        "Raw LLM Request",
        turn ? `Turn ${turn}` : `#${index + 1}`,
        modelName ? `(${modelName})` : "",
    ].filter(Boolean);

    return {
        title: titleParts.join(" "),
        body: requestBody,
    };
}

function getRawLlmRequestFromMetadata(metadata: unknown) {
    if (typeof metadata === "string") {
        return metadata;
    }

    if (!isRecord(metadata)) {
        return "";
    }

    return (
        getStringProperty(metadata, "rawLlmRequest", "RawLlmRequest") ||
        getStringProperty(metadata, "requestText", "RequestText")
    );
}

function formatMetadata(metadata: unknown) {
    if (typeof metadata === "string") {
        return metadata;
    }

    return JSON.stringify(metadata, null, 2);
}

function formatMetadataForDisplay(metadata: unknown) {
    if (!isRecord(metadata)) {
        return formatMetadata(metadata);
    }

    const displayMetadata = { ...metadata };
    delete displayMetadata.rawPrompt;
    delete displayMetadata.RawPrompt;

    if (Object.keys(displayMetadata).length === 0) {
        return "";
    }

    return JSON.stringify(displayMetadata, null, 2);
}

function getRawPromptMetadata(metadata: unknown) {
    if (!isRecord(metadata)) {
        return null;
    }

    const debugContentType = getStringProperty(
        metadata,
        "debugContentType",
        "DebugContentType",
    );
    const rawPrompt =
        getStringProperty(metadata, "rawPrompt", "RawPrompt") ||
        getStringProperty(metadata, "prompt", "Prompt");

    if (debugContentType !== "RawPrompt" || !rawPrompt) {
        return null;
    }

    return {
        rawPrompt,
        source: getStringProperty(metadata, "source", "Source"),
        activityName: getStringProperty(metadata, "activityName", "ActivityName"),
    };
}

function getRawPromptTitle(
    entry: DebugEntry,
    metadata: NonNullable<ReturnType<typeof getRawPromptMetadata>>,
) {
    return [
        metadata.source ? `${metadata.source} raw prompt` : "Raw prompt",
        entry.message || metadata.activityName,
    ]
        .filter(Boolean)
        .join(" - ");
}

function formatTimestamp(timestampUtc?: string) {
    if (!timestampUtc) {
        return "";
    }

    const timestamp = new Date(timestampUtc);
    if (Number.isNaN(timestamp.getTime())) {
        return timestampUtc;
    }

    return timestamp.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
    });
}

function formatMilliseconds(milliseconds?: number | null) {
    return typeof milliseconds === "number"
        ? `${milliseconds.toLocaleString()} ms`
        : "";
}

function getCompactDescription(entry: DebugEntry) {
    const rawPromptMetadata = getRawPromptMetadata(entry.metadata);
    if (rawPromptMetadata) {
        return rawPromptMetadata.source
            ? `${rawPromptMetadata.source} raw prompt available`
            : "raw prompt available";
    }

    if (entry.category !== "Filters") {
        return "";
    }

    const aggregateDescription = getAggregateFilterOverrideDescription(
        entry.metadata,
    );

    if (aggregateDescription) {
        return aggregateDescription;
    }

    if (!entry.message?.startsWith("Applied tool filter override:")) {
        return "";
    }

    const filterName = entry.message
        .replace("Applied tool filter override:", "")
        .trim();

    const value =
        isRecord(entry.metadata) && "value" in entry.metadata
            ? entry.metadata.value
            : entry.metadata;

    return `${filterName}: ${summarizeValue(value)}`;
}

function getAggregateFilterOverrideDescription(metadata: unknown) {
    if (!isRecord(metadata) || !Array.isArray(metadata.overrides)) {
        return "";
    }

    return metadata.overrides
        .map((filterOverride) => {
            if (!isRecord(filterOverride)) {
                return "";
            }

            const filterName = getStringProperty(
                filterOverride,
                "filterName",
                "FilterName",
            );

            if (!filterName) {
                return "";
            }

            const value =
                "value" in filterOverride
                    ? filterOverride.value
                    : filterOverride.Value;

            return `${filterName}: ${summarizeValue(value)}`;
        })
        .filter(Boolean)
        .join("; ");
}

function getToolLabelsForEntry(entry: DebugTraceEntry) {
    const executedToolLabels = collectToolLabels(entry, [
        "publicToolCalls",
        "toolCalls",
        "functionCalls",
    ]);

    const labels =
        executedToolLabels.length > 0
            ? executedToolLabels
            : collectToolLabels(entry, ["loadedTools", "newTools"]);

    return summarizeToolLabels(labels);
}

function collectToolLabels(
    entry: DebugTraceEntry,
    metadataKeys: string[],
): string[] {
    const labels = [
        ...getToolLabelsFromMetadata(entry.metadata, metadataKeys),
        ...entry.children.flatMap((child) =>
            collectToolLabels(child, metadataKeys),
        ),
    ];

    return labels.filter((label, index) => labels.indexOf(label) === index);
}

function getToolLabelsFromMetadata(
    metadata: unknown,
    metadataKeys: string[],
): string[] {
    if (!isRecord(metadata)) {
        return [];
    }

    return metadataKeys.flatMap((metadataKey) => {
        const value = metadata[metadataKey] ?? metadata[toPascalCase(metadataKey)];
        if (!Array.isArray(value)) {
            return [];
        }

        return value.map(getToolLabel).filter((label): label is string =>
            Boolean(label),
        );
    });
}

function getToolLabel(value: unknown): string | undefined {
    if (typeof value === "string") {
        return value;
    }

    if (!isRecord(value)) {
        return undefined;
    }

    const functionValue = value.function ?? value.Function;
    if (isRecord(functionValue)) {
        const functionName = getStringProperty(functionValue, "name", "Name");
        if (functionName) {
            return functionName;
        }
    }

    return (
        getStringProperty(value, "functionName", "FunctionName") ||
        getStringProperty(value, "name", "Name")
    );
}

function summarizeToolLabels(labels: string[]) {
    const visibleLabels = labels.slice(0, 4);
    if (labels.length > visibleLabels.length) {
        visibleLabels.push(`+${labels.length - visibleLabels.length}`);
    }

    return visibleLabels;
}

function toPascalCase(value: string) {
    return value.length === 0
        ? value
        : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function getStringProperty(
    value: Record<string, unknown>,
    camelCaseProperty: string,
    pascalCaseProperty: string,
) {
    const propertyValue =
        value[camelCaseProperty] ?? value[pascalCaseProperty];

    return typeof propertyValue === "string" ? propertyValue : "";
}

function getNumberProperty(
    value: Record<string, unknown>,
    camelCaseProperty: string,
    pascalCaseProperty: string,
) {
    return (
        getOptionalNumberProperty(value, camelCaseProperty, pascalCaseProperty) ??
        0
    );
}

function getOptionalNumberProperty(
    value: Record<string, unknown>,
    camelCaseProperty: string,
    pascalCaseProperty: string,
) {
    const propertyValue =
        value[camelCaseProperty] ?? value[pascalCaseProperty];

    return typeof propertyValue === "number" ? propertyValue : undefined;
}

function getNumberRecordProperty(
    value: Record<string, unknown>,
    camelCaseProperty: string,
    pascalCaseProperty: string,
) {
    const propertyValue =
        value[camelCaseProperty] ?? value[pascalCaseProperty];
    const counts: Record<string, number> = {};

    if (!isRecord(propertyValue)) {
        return counts;
    }

    Object.entries(propertyValue).forEach(([key, count]) => {
        if (typeof count === "number") {
            counts[key] = count;
        }
    });

    return counts;
}

function mergeAdditionalTokenCounts(
    left: Record<string, number>,
    right: Record<string, number>,
) {
    const result = { ...left };

    Object.entries(right).forEach(([key, value]) => {
        result[key] = (result[key] ?? 0) + value;
    });

    return result;
}

function summarizeValue(value: unknown) {
    if (value === null || value === undefined) {
        return "";
    }

    if (Array.isArray(value)) {
        return value.map(summarizeValue).join(", ");
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}
